import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { books, allImprints, allGenres } from "../data/books";
import { BookCard } from "../components/BookCard";
import { SEO } from "../components/SEO";

export default function Books() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [imprint, setImprint] = useState(searchParams.get("imprint") || "all");
  const [genre, setGenre] = useState("all");

  useEffect(() => {
    setImprint(searchParams.get("imprint") || "all");
  }, [searchParams]);

  const filtered = useMemo(() => {
    return books.filter((b) => {
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        b.title.toLowerCase().includes(q) ||
        b.subtitle?.toLowerCase().includes(q) ||
        b.genres.some((g) => g.toLowerCase().includes(q));
      const matchesImprint = imprint === "all" || b.imprint === imprint;
      const matchesGenre = genre === "all" || b.genres.includes(genre);
      return matchesQuery && matchesImprint && matchesGenre;
    });
  }, [query, imprint, genre]);

  const changeImprint = (v) => {
    setImprint(v);
    if (v === "all") {
      searchParams.delete("imprint");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ imprint: v }, { replace: true });
    }
  };

  return (
    <div data-testid="books-page">
      <SEO
        title="Books"
        path="/books"
        description={`Every CBH title — ${books.length} books across biography, wisdom, self-help, women leadership, society, geo-politics, children, and business. Filter by imprint, genre, or search.`}
        keywords="CBH books, Indian publisher catalogue, buy Indian biographies, Bharat Icon Series, self-help India, women leadership books, geopolitics India"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Conlang Book House — Full Catalogue",
          description: `The complete catalogue of ${books.length} Conlang Book House titles.`,
          url: "https://conlangbookhouse.com/books",
          isPartOf: { "@type": "WebSite", name: "Conlang Book House", url: "https://conlangbookhouse.com" },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: books.length,
            itemListElement: books.slice(0, 30).map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://conlangbookhouse.com/books/${b.slug}`,
              name: b.title,
            })),
          },
        }}
      />
      {/* Header */}
      <section className="pt-16 lg:pt-24 pb-12 grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ The Catalogue</p>
          <h1 className="font-editorial text-6xl md:text-8xl leading-[0.9] tracking-tighter text-ink text-balance">
            Every book we&rsquo;ve <em className="text-crimson not-italic">signed.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink-muted">
            Filter by imprint, genre-blend, or search title. Each book opens onto its own
            page, with author bio, ISBN, table of contents, IP surface, and both Amazon
            and direct CBH checkout.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-crimson/15 bg-ivory-soft py-6 sticky top-20 z-30 backdrop-blur-md" data-testid="books-filters">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-wrap items-center gap-4">
          <input
            type="text"
            placeholder="Search title, subject, subtitle…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="books-search-input"
            className="flex-1 min-w-[240px] bg-ivory border border-crimson/20 px-4 py-3 text-sm placeholder:text-ink-muted/60 focus:outline-none focus:border-crimson"
          />
          <select
            value={imprint}
            onChange={(e) => changeImprint(e.target.value)}
            data-testid="books-imprint-filter"
            className="bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson"
          >
            <option value="all">All Imprints</option>
            {allImprints.map((i) => (
              <option key={i} value={i}>{i}</option>
            ))}
          </select>
          <select
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            data-testid="books-genre-filter"
            className="bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson"
          >
            <option value="all">All Genres</option>
            {allGenres.map((g) => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
          <p className="text-xs uppercase tracking-[0.22em] text-ink-muted ml-auto" data-testid="books-count">
            {filtered.length} {filtered.length === 1 ? "title" : "titles"}
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 lg:py-24" data-testid="books-grid">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {filtered.length === 0 ? (
            <div className="py-24 text-center">
              <p className="font-editorial italic text-3xl text-ink-muted">
                No books match — yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-12 lg:gap-16">
              {filtered.map((b, i) => (
                <BookCard key={b.slug} book={b} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
